import { useCallback, useEffect, useState } from 'react';
import { Bell, FileDown, Mail, Play, X } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadingState } from '@/components/ui/loading-state';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePermission } from '@/components/rbac/use-permission';
import { P } from '@/lib/rbac/permissions.generated';
import { billingApi, saveBlob, type DunningNotice } from '@/lib/api/billing';
import { formatCents, formatIsoDate } from '@/lib/billing';

const MAHNWESEN = P.buchhaltung.ruecklastschriften_und_mahnwesen;
const STATUS_LABEL: Record<string, string> = {
  proposed: 'Vorschlag',
  issued: 'Freigegeben',
  cancelled: 'Verworfen',
};

/**
 * Mahnwesen (ADR-0003, Phase 7): Der Mahnlauf schlägt vor, das Büro gibt frei. Erst mit der
 * Freigabe entstehen Mahnnummer, Mahngebühr, Schreiben und — ab der eingestellten Stufe —
 * die Einzugssperre.
 */
export default function DunningNoticesPage() {
  const canEdit = usePermission(MAHNWESEN.mahnwesen_bearbeiten);
  const canIssue = usePermission(MAHNWESEN.mahnungen_erstellen_versenden);
  const [notices, setNotices] = useState<DunningNotice[]>([]);
  const [status, setStatus] = useState('proposed');
  // Leer heißt heute — der Lauf verhält sich dann wie bisher.
  const [stichtag, setStichtag] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState<DunningNotice | null>(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await billingApi.listDunningNotices({
        status: status === 'all' ? undefined : status,
      });
      setNotices(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Mahnungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async () => {
    setBusy(true);
    try {
      // Ohne Stichtag rechnet der Lauf mit heute — genau wie bisher.
      const summary = await billingApi.runDunning(stichtag ? { on: stichtag } : {});
      const levels = Object.entries(summary.byLevel)
        .map(([level, count]) => `${count}× Stufe ${level}`)
        .join(', ');
      const zumStichtag = stichtag ? ` zum ${formatIsoDate(stichtag)}` : '';
      toast.success(
        summary.proposals > 0
          ? `${summary.proposals} Vorschläge${zumStichtag} (${levels})`
          : `Keine überfälligen Vertragskonten${zumStichtag}`,
      );
      if (summary.resetAccounts > 0) {
        toast.message(`${summary.resetAccounts} bezahlte Konten zurückgesetzt`);
      }
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Mahnlauf fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const issue = async (notice: DunningNotice, send: boolean) => {
    setBusy(true);
    try {
      const result = await billingApi.issueDunningNotice(notice.id, send);
      toast.success(
        result.emailSent
          ? `${notice.levelLabel} ${result.number} verschickt`
          : `${notice.levelLabel} ${result.number} freigegeben — bitte ausdrucken`,
      );
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Freigabe fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const download = async (notice: DunningNotice) => {
    setBusy(true);
    try {
      const file = await billingApi.downloadDunningNotice(
        notice.id,
        notice.fileName ?? `mahnung-${notice.id}.pdf`,
      );
      saveBlob(file.blob, file.filename);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Schreiben konnte nicht geladen werden');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Mahnwesen</h1>
        <p className="text-muted-foreground">
          Der Mahnlauf schlägt Mahnungen vor, das Büro gibt frei. Mit der Freigabe entstehen
          Mahnnummer, Mahngebühr und das Schreiben; ab der eingestellten Stufe ruht der Einzug.
          Fristen und Gebühren stellt jeder Gläubiger in der Abrechnung ein.
        </p>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-56" aria-label="Status filtern">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="proposed">Vorschläge</SelectItem>
            <SelectItem value="issued">Freigegeben</SelectItem>
            <SelectItem value="cancelled">Verworfen</SelectItem>
            <SelectItem value="all">Alle</SelectItem>
          </SelectContent>
        </Select>
        {canEdit && (
          <div className="flex items-end gap-2">
            {/* Stichtag: leer heißt heute. Ein Lauf auf ein anderes Datum ging bisher
                nur über die API — beim Nacharbeiten und im Test braucht man ihn. */}
            <div className="space-y-1">
              <Label htmlFor="mahnlauf-stichtag" className="text-xs text-muted-foreground">
                Stichtag
              </Label>
              <Input
                id="mahnlauf-stichtag"
                type="date"
                className="w-40"
                value={stichtag}
                onChange={(event) => setStichtag(event.target.value)}
              />
            </div>
            <Button onClick={() => void run()} disabled={busy}>
              <Play className="mr-2 h-4 w-4" />
              Mahnlauf starten
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <LoadingState message="Mahnungen werden geladen …" />
      ) : notices.length === 0 ? (
        <EmptyState
          message={
            status === 'proposed'
              ? 'Keine offenen Vorschläge. Der Mahnlauf läuft täglich um 06:00.'
              : 'Keine Mahnungen in dieser Ansicht.'
          }
        />
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Empfänger</TableHead>
                <TableHead>Stufe</TableHead>
                <TableHead>Fällig seit</TableHead>
                <TableHead className="text-right">Offen</TableHead>
                <TableHead className="text-right">Gebühr</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {notices.map((notice) => (
                <TableRow key={notice.id}>
                  <TableCell>
                    <div className="font-medium">{notice.recipientName}</div>
                    <div className="text-xs text-muted-foreground">
                      {notice.number ? `${notice.number} · ` : ''}
                      {notice.recipientEmail ?? 'keine E-Mail — Ausdruck nötig'}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {notice.levelLabel}
                    {notice.blocksCollection && (
                      <Badge variant="warning" size="sm" className="ml-2">
                        Einzug ruht
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm tabular-nums">
                    {formatIsoDate(notice.oldestDueDate)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCents(notice.openCents)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {notice.feeCents > 0 ? formatCents(notice.feeCents) : '–'}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        notice.status === 'issued'
                          ? 'success'
                          : notice.status === 'cancelled'
                            ? 'secondary'
                            : 'info'
                      }
                      size="sm"
                    >
                      {STATUS_LABEL[notice.status] ?? notice.status}
                    </Badge>
                    {notice.sentAt && (
                      <div className="text-xs text-muted-foreground">
                        verschickt {formatIsoDate(notice.sentAt.slice(0, 10))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {notice.status === 'proposed' && canIssue && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void issue(notice, true)}
                            disabled={busy}
                          >
                            <Mail className="mr-2 h-4 w-4" />
                            Freigeben und senden
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => void issue(notice, false)}
                            disabled={busy}
                          >
                            <Bell className="mr-1 h-4 w-4" />
                            Nur freigeben
                          </Button>
                        </>
                      )}
                      {notice.status === 'proposed' && canEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setReason('');
                            setCancelling(notice);
                          }}
                          disabled={busy}
                        >
                          <X className="mr-1 h-4 w-4" />
                          Verwerfen
                        </Button>
                      )}
                      {notice.status === 'issued' && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => void download(notice)}
                            disabled={busy}
                          >
                            <FileDown className="mr-1 h-4 w-4" />
                            PDF
                          </Button>
                          {canIssue && notice.recipientEmail && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                setBusy(true);
                                try {
                                  const result = await billingApi.sendDunningNotice(notice.id);
                                  toast.success(
                                    result.emailSent ? 'Mahnung verschickt' : 'Kein Versand möglich',
                                  );
                                  await load();
                                } catch (error) {
                                  toast.error(
                                    error instanceof Error ? error.message : 'Versand fehlgeschlagen',
                                  );
                                } finally {
                                  setBusy(false);
                                }
                              }}
                              disabled={busy}
                            >
                              <Mail className="mr-1 h-4 w-4" />
                              Erneut senden
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {cancelling && (
        <Dialog open onOpenChange={(open) => !open && setCancelling(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Vorschlag verwerfen?</DialogTitle>
              <DialogDescription>
                {cancelling.levelLabel} für {cancelling.recipientName}. Der nächste Mahnlauf darf
                danach wieder vorschlagen.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="dunning-cancel-reason">Grund</Label>
              <Input
                id="dunning-cancel-reason"
                maxLength={200}
                placeholder="z. B. Ratenzahlung vereinbart"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelling(null)} disabled={busy}>
                Abbrechen
              </Button>
              <Button
                variant="destructive"
                disabled={busy || !reason.trim()}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await billingApi.cancelDunningNotice(cancelling.id, reason.trim());
                    toast.success('Vorschlag verworfen');
                    setCancelling(null);
                    await load();
                  } catch (error) {
                    toast.error(error instanceof Error ? error.message : 'Verwerfen fehlgeschlagen');
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Verwerfen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
